import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useAppContext } from "./hooks/useAppContext";
import "./MiniDonations.scss";
import useToast from "./hooks/useToast";
import Fade from "./Components/Fade";
import { TransitionGroup } from "react-transition-group";

import {
  createOrderItems,
  fetchFundForCart,
  updateOrderItem,
} from "./services/cart.service";
import {lazyWithRetry} from "./helpers/lazyWithRetry";

const DonationsForm = lazyWithRetry(() => import("./Components/DonationsForm"));
const FrequencyToggle = lazyWithRetry(() => import("./Components/FrequencyToggle"));

// @todo group this with other portals in a subfolder
export default function MiniDonations({ element, fundId }) {
  const {
    frequency,
    cart,
    setFrequency,
    items,
    setEditStateItems,
    setIsLoading,
    editStateItems,
    setLoadOrderItemList,
    isLoading,
  } = useAppContext();

  const [item, setItem] = useState(null);
  // these items are not the same as orderItems
  const [fundItems, setFundItems] = useState([]);
  useEffect(() => {
    if (fundId) {
      fetchFundForCart(fundId).then((res) => {
        res.data.forEach((item) => {
          if (item.type === "order--donation") {
            setFrequency(item.attributes.frequency);
          }
        });
        setFundItems(res.data);
      });
    }
  }, [fundId, cart]);

  useEffect(() => {
    if (!item || !items.length) return;
    const currItem = items.find((i) => i.id === item.id);
    const price = currItem?.attributes.total_price?.formatted;
    if (price) {
      setItem({
        ...item,
        attributes: {
          ...item.attributes,
          total_price: { ...item.attributes.total_price, formatted: price },
        },
      });
    }
  }, [items]);

  useEffect(() => {
    const preset = fundItems.find((i) => i.type === "preset--preset") || {};
    const fund = fundItems.find((i) => i.type === "fund--fund") || {};
    const order = fundItems.find((i) => i.type === "order--donation") || {};
    const orderItem = fundItems.find((i) => i.type === "order-item--donation");

    if (orderItem)
      return setItem({
        ...orderItem,
        attributes: { ...orderItem.attributes, ...preset.attributes },
      });
    // Add presets to fund
    const _item = {
      ...fund,
      attributes: {
        ...fund.attributes,
        ...preset.attributes,
      },
      relationships: {
        ...fund.relationships,
        ...order.relationships,
      },
    };

    if (fundItems.length) {
      setItem(_item);
    }
  }, [fundItems]);

  async function onClickSubmit(amount) {
    const notifyCreateOrderItemError = useToast({
      type: "error",
      id: "createOrderItem",
      actionText: "RETRY?",
      action: async () => await onClickSubmit(amount),
      dismissable: true,
      message: "There was a problem creating your donation.",
    });
    const shouldUpdateOrder = cart?.relationships.order_items?.data?.some(
      (i) => i.id === item.id
    );
    if (shouldUpdateOrder) {
      await updateOrderItem(item, amount, frequency);
    } else {
      await createOrderItems({
        amt: amount,
        fund_id: item.id,
        freq: frequency,
      })
        .then((response) => {
          return true;
        })
        .finally((response) => {
          setLoadOrderItemList(true);
          setIsLoading(false);
          setEditStateItems([...editStateItems, response[0].id]);
        })
        .catch((err) => {
          notifyCreateOrderItemError(err);
        });
    }
    window.location.href = "/give/checkout";
  }

  function onSubmitItem(item, amount) {
    const shouldUpdateOrder = cart?.relationships.order_items?.data?.some(
      (i) => i.id === item.id
    );

    if (shouldUpdateOrder) {
      return updateOrderItem(item, amount, frequency);
    }
    return createOrderItems({
      amt: amount,
      fund_id: item.id,
      freq: frequency,
    });
  }

  return createPortal(
    <TransitionGroup component={null}>
      <Fade toggle={item && isLoading} timeout={200}>
        <div className="mini-donations-spinner">
          <div className="loader-overlay">
            <div className="spinner spinner--centered"></div>
          </div>
        </div>
      </Fade>

      <Fade toggle={item && !isLoading} timeout={200}>
        <div className="mini-donations-form">
          <FrequencyToggle isMiniCart />
          <DonationsForm
            isMiniCart
            setEditStateItems={setEditStateItems}
            item={item}
            onClickSubmit={onClickSubmit}
            onSubmitItem={onSubmitItem}
          ></DonationsForm>
        </div>
      </Fade>
    </TransitionGroup>,
    element
  );
}
